/**
 * Locale-aware formatting for the chrome (docs/TRANSLATION_SPEC.md §13).
 *
 * Dates, times and numbers go through `Intl` with the ACTIVE UI language, so a
 * Spanish-reading parent sees "jue, 2 oct" and "16:00", not "Thu, Oct 2". Never
 * call `toLocaleDateString()` with no locale in chrome code — it follows the
 * browser, not the user's choice.
 *
 * Relative times return a catalog key plus its count instead of a string: the
 * words live in `common.json` (`time.*`) where translators can reach them.
 */
import { activeUiLanguage } from './index';

/** The BCP-47 tag to hand `Intl`. */
export const activeLocale = (): string => activeUiLanguage();

const dateTime = (options: Intl.DateTimeFormatOptions, locale?: string) =>
  new Intl.DateTimeFormat(locale || activeLocale(), options);

/** "Oct 2" / "2 oct" — add `year` yourself when the year matters. */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
  locale?: string
): string => dateTime(options, locale).format(date);

/** "Thu, Oct 2" / "jue, 2 oct". */
export const formatWeekdayDate = (date: Date, locale?: string): string =>
  dateTime({ weekday: 'short', month: 'short', day: 'numeric' }, locale).format(date);

/** "4:00 PM" / "16:00". */
export const formatTime = (date: Date, locale?: string): string =>
  dateTime({ hour: 'numeric', minute: '2-digit' }, locale).format(date);

/** "Oct 2, 4:00 PM" / "2 oct, 16:00". */
export const formatDateTime = (date: Date, locale?: string): string =>
  dateTime({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, locale).format(
    date
  );

/** "October 2, 2026" / "2 de octubre de 2026" — for documents and receipts. */
export const formatLongDate = (date: Date, locale?: string): string =>
  dateTime({ year: 'numeric', month: 'long', day: 'numeric' }, locale).format(date);

export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
  locale?: string
): string => new Intl.NumberFormat(locale || activeLocale(), options).format(value);

/** A `common.json` `time.*` key and the count it interpolates. */
export interface RelativeTimeLabel {
  key: string;
  count: number;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * How long ago, as a key to translate. `short` drops the "ago" for tight
 * spots like the notification bell ("5m"); the default reads "5m ago".
 *
 * Usage: `const { key, count } = timeAgo(date); t(key, { count })`.
 */
export const timeAgo = (
  date: Date,
  { now = new Date(), short = false }: { now?: Date; short?: boolean } = {}
): RelativeTimeLabel => {
  const prefix = short ? 'time.short' : 'time.ago';
  const elapsed = Math.max(0, now.getTime() - date.getTime());

  if (elapsed < MINUTE) return { key: 'time.justNow', count: 0 };
  if (elapsed < HOUR) return { key: `${prefix}Minutes`, count: Math.floor(elapsed / MINUTE) };
  if (elapsed < DAY) return { key: `${prefix}Hours`, count: Math.floor(elapsed / HOUR) };
  if (elapsed < WEEK) return { key: `${prefix}Days`, count: Math.floor(elapsed / DAY) };
  if (elapsed < 4 * WEEK) return { key: `${prefix}Weeks`, count: Math.floor(elapsed / WEEK) };
  return { key: `${prefix}Months`, count: Math.max(1, Math.floor(elapsed / (30 * DAY))) };
};

/** Whole days from today: negative in the past, 0 today, 1 tomorrow. */
export const daysFromToday = (date: Date, now: Date = new Date()): number => {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOf(date) - startOf(now)) / DAY);
};
